/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

// 3rd party libraries
import React from 'react';
import { Flex, Popover, Switch } from 'antd';

// Local imports
import { Worker } from '@/generated/graphql';
import moment from 'moment';
import { userSelector } from '@/stores/slices/user';
import { useAppSelector } from '@/stores/hooks';
import { getWorkerLogLink } from '@/utils/internalTools';
import AppUtil from '../utils/AppUtil';

interface IPropsRunDebugDetails {
  worker: Worker;
  children: any;
  showCreatedAt?: boolean;
  currentDocumentToken?: string;
  isDebugMode?: boolean;
  handleDebugMode?: (start: boolean) => void;
}

function RunDebugDetails(props: IPropsRunDebugDetails) {
  const {
    worker,
    children,
    showCreatedAt,
    currentDocumentToken,
    isDebugMode,
    handleDebugMode
  } = props;

  const mmt = moment(
    Number(AppUtil.millisFromDateString(worker?.createdAt ?? Date().toString()))
  );

  const logLink = getWorkerLogLink(
    worker.id,
    mmt.clone().subtract(5, 'minute'),
    moment().add(5, 'minute')
  );

  const { username } = useAppSelector(userSelector);
  const isSupportUser = AppUtil.isSupportUser(username);

  return (
    <Popover
      key={`details-${worker?.id}`}
      placement="right"
      className="popover-trigger"
      title={worker?.name || 'Playground'}
      trigger="hover"
      content={
        <span>
          <strong>Id:</strong> {worker?.id} (
          <a href={logLink} target="_blank" rel="noreferrer">
            logs
          </a>
          )
          <p />
          {showCreatedAt ? (
            <span>
              <strong>Created At:</strong> {mmt.toLocaleString()}
            </span>
          ) : null}
          <p />
          {isSupportUser && handleDebugMode && (
            <Flex align="center" gap={8}>
              <strong>Debug mode:</strong>
              <Switch
                defaultChecked={isDebugMode}
                data-cy="debug-mode-switch"
                onChange={handleDebugMode}
              />
              {isDebugMode && (
                <a
                  href="https://www.notion.so/kognitos/UI-Debugger-a99e56699e9b42bf8ac9d4eff760751e"
                  target="_blank"
                  rel="noreferrer"
                >
                  View instructions to use
                </a>
              )}
            </Flex>
          )}
          <p />
          {isSupportUser && currentDocumentToken ? (
            <span>
              <strong>Document Token:</strong> {currentDocumentToken}
            </span>
          ) : null}
        </span>
      }
    >
      {children}
    </Popover>
  );
}

export default RunDebugDetails;
